<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <v-btn
                class="mx-2"
                fab
                x-small
                color="primary"
                @click="$router.back()"
              >
                <v-icon dark> mdi-arrow-left </v-icon>
              </v-btn>
              <h3 class="font-weight-bold">Stock Details Per User</h3>
              <v-spacer></v-spacer>
              <v-btn
                rounded
                color="primary"
                dark
                @click="refreshData"
                :loading="isLoading"
              >
                <v-icon left>mdi-refresh</v-icon>
                Refresh
              </v-btn>
            </v-card-title>

            <!-- Summary Cards -->
            <v-card-text>
              <v-row>
                <v-col sm="4">
                  <v-card color="primary" dark>
                    <v-card-text>
                      <div class="text-h4">{{ summary.total_users_with_stock || 0 }}</div>
                      <div>Users with Stock</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col sm="4">
                  <v-card color="success" dark>
                    <v-card-text>
                      <div class="text-h4">{{ summary.total_assigned_quantity || 0 }}</div>
                      <div>Total Assigned Quantity</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col sm="4">
                  <v-card color="info" dark>
                    <v-card-text>
                      <div class="text-h4">{{ summary.total_assigned_variants || 0 }}</div>
                      <div>Total Assigned Variants</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- User Stock Details -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>

              <div v-else-if="stockDetails.length === 0" class="text-center py-5">
                <v-icon large color="grey">mdi-package-variant-closed</v-icon>
                <h3 class="grey--text">No Stock Assignments Found</h3>
                <p class="grey--text">No users currently have assigned stock.</p>
              </div>

              <v-expansion-panels v-else multiple>
                <v-expansion-panel
                  v-for="userStock in stockDetails"
                  :key="`user-${userStock.user_id}`"
                >
                  <v-expansion-panel-header>
                    <div class="d-flex align-center">
                      <v-avatar color="primary" class="mr-3">
                        <span class="white--text">{{ userStock.user_name.charAt(0) }}</span>
                      </v-avatar>
                      <div>
                        <div class="font-weight-bold">{{ userStock.user_name }}</div>
                        <div class="caption grey--text">{{ userStock.position }} • {{ userStock.user_email }}</div>
                      </div>
                      <v-spacer></v-spacer>
                      <div class="text-right mr-4">
                        <v-chip small color="primary" text-color="white">
                          {{ userStock.total_quantity }} Units
                        </v-chip>
                        <br>
                        <span class="caption grey--text">{{ userStock.total_variants }} Variants</span>
                      </div>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content>
                    <v-simple-table>
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left">Product</th>
                            <th class="text-left">Variant</th>
                            <th class="text-left">Quantity</th>
                            <th class="text-left">Assigned Date</th>
                            <th class="text-left">Assigned By</th>
                            <th class="text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="stock in userStock.stock_details"
                            :key="`stock-${stock.id}`"
                          >
                            <td>{{ stock.product_name }}</td>
                            <td>
                              <v-chip small outlined>
                                {{ stock.variant_details }}
                              </v-chip>
                            </td>
                            <td>
                              <v-chip small color="success" text-color="white">
                                {{ stock.quantity }}
                              </v-chip>
                            </td>
                            <td>{{ stock.assigned_date | niceDateTime }}</td>
                            <td>{{ stock.assigned_by }}</td>
                            <td>
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                  <v-btn
                                    v-bind="attrs"
                                    v-on="on"
                                    fab
                                    x-small
                                    color="primary"
                                    :to="`/assigned_stocks/${stock.id}`"
                                  >
                                    <v-icon>mdi-pencil</v-icon>
                                  </v-btn>
                                </template>
                                <span>Edit Assignment</span>
                              </v-tooltip>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "StockDetailsPerUser",
  data() {
    return {
      stockDetails: [],
      summary: {},
      isLoading: false,
    };
  },
  mounted() {
    this.fetchStockDetails();
  },
  methods: {
    async fetchStockDetails() {
      this.isLoading = true;
      try {
        const response = await axios.get("/assigned_stocks/stock-details-per-user");
        this.stockDetails = response.data.data || [];
        this.summary = response.data.summary || {};
      } catch (error) {
        console.error("Error fetching stock details:", error);
        if (this.$toast) {
          this.$toast.error("Error fetching stock details");
        }
      } finally {
        this.isLoading = false;
      }
    },
    refreshData() {
      this.fetchStockDetails();
    },
    formatDate(date) {
      return moment(date).format("DD-MM-YYYY HH:mm");
    },
  },
};
</script>

<style scoped>
.v-expansion-panel-header {
  min-height: 72px;
}
</style>